import {BaseElement, BASIS, OSkinableInit} from "back/commons/basis";
import {IReg, REG} from "lib/commons/registry";
import {TxtElement} from "back/edit/wed/wedlets/txt/txtTags";
import {JRemoteResDescFromClient, REMOTE, RemoteRes} from "lib/core/remote";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IWspUiEnv} from "lib/wsp/wsp";
import {DOMSH} from "lib/commons/xml/domsh";
import {IBasicUniversePointer} from "lib/core/universe";
import {POPUP} from "back/commons/widgets/popups";
import {Button} from "back/commons/widgets/buttons";
import {OpenMetaDialog} from "back/edit/wed/wedlets/txt/txtActions";
import {LANG} from "lib/commons/lang";
import {ORemoteResRendererInit, RemoteResRenderer} from "back/core/widgets/remoteResRenderer";


/**
 * Affichage des metas du texte contenant un lien Remote.
 * Cf sous-classes en fonction des différents contextes d'appels (tooltip, inline, block).
 */
export interface TxtMetaRemote extends BaseElement {
	initialize(init: OTxtMetaRemoteInit): this
}

export interface OTxtMetaRemoteInit extends OSkinableInit {
	reg?: IReg<IWspUiEnv>
	txtElt: TxtElement
	remoteResSelector?: (reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient) => RemoteRes | null
	/** Construction du titre. Si null ou ne retourne pas 'done', le titre =  RemoteRes.title || RemoteRes.url || "[url non renseignée]" */
	buildTitle?: (this: TxtMetaRemote, target: HTMLSpanElement, txtElt: TxtElement, resMetas: JRemoteResDescFromClient | null) => 'done' | void
	preview?: 'shown'/*par défaut*/ | 'none' | 'tooltip'
	previewInit?: ORemoteResRendererInit
	metaBtn?: 'shown'/*par défaut*/ | 'none'
	openBtn?: 'shown'/*par défaut*/ | 'none'
}

export abstract class TxtMetaRemote extends BaseElement {

	protected config: OTxtMetaRemoteInit;

	protected titleRoot?: HTMLSpanElement;
	protected previewRoot?: RemoteResRenderer;
	protected metaBtn: Button;
	protected openBtn: Button;

	protected lastRemoteUrl: string | null;
	protected lastRemoteDesc?: JRemoteResDescFromClient | null;

	/** Recherche le 1er lien Remote dans les metas. */
	protected extractRemoteUrl(): string | null {
		const metas = this.config.txtElt.metas;
		return metas ? DOM.findNext(metas, metas, (n): n is Element => n.nodeName === "sc:location")?.textContent : null;
	}

	protected async fetchResDescFromClient(url: string): Promise<JRemoteResDescFromClient> {
		return (await REMOTE.fetchResDesc(url, this.config.reg)) || {url, status: -1};
	}

	async _refresh() {
		const newUrl = this.extractRemoteUrl();
		if (newUrl != this.lastRemoteUrl) {
			this.lastRemoteUrl = newUrl;
			this.lastRemoteDesc = null;
			this.internalRefresh();
			if (newUrl) {
				const newDesc = await this.fetchResDescFromClient(newUrl);
				if (this.lastRemoteUrl !== newUrl) return; //raceCond
				this.lastRemoteDesc = newDesc;
			}
		}
		this.internalRefresh();
	}

	protected internalRefresh() {
		if (this.titleRoot) this.updateTitle();
		if (this.previewRoot) this.updatePreview();
		if (this.openBtn) this.updateopenBtn();
	}


	/** Rafraichit la zone de Titre */
	protected updateTitle() {
		if (this.config.buildTitle?.call(this, this.titleRoot, this.config.txtElt, this.lastRemoteDesc) == 'done') return;
		DOM.setTextContent(this.titleRoot, this.lastRemoteDesc?.title || this.lastRemoteUrl || "[URL non renseignée]");
	}

	/** Rafraichit la zone de Preview */
	protected updatePreview() {
		const st = this.lastRemoteDesc?.status || 0;
		if (st < 200 || st > 299) {
			DOM.setHiddenProp(this.previewRoot, true);
			return;
		}
		DOM.setHiddenProp(this.previewRoot, false);
		this.previewRoot.setRemoteRes(this.lastRemoteDesc ? this.config.remoteResSelector(this.config.reg, this.lastRemoteDesc) : null);
	}

	/** Rafraichit l'état du bouton d'édition de la ressource */
	protected updateopenBtn() {
		DOM.setHiddenProp(this.openBtn, this.findOpenView() == null);
	}

	protected findOpenView() {
		const editor = REMOTE.findEditorInResDesc(this.lastRemoteDesc);
		if (editor) return editor;
		//Recherche d'une view complète différente de celle affichéee preview.
		const view = REMOTE.findFullViewInResDesc(this.lastRemoteDesc);
		if (!view) return null;
		const previewTags = this.previewRoot?.resShown?.getTags();
		return !LANG.arrayEquals(previewTags.body, view.tags?.body) || !LANG.arrayEquals(previewTags.web, view.tags?.web) ? view : null;
	}

	protected _initialize(init: OTxtMetaRemoteInit) {
		if (!init.reg) init.reg = init.txtElt.wedMgr.reg;
		if (!init.remoteResSelector) {
			//Affectation du remoteResSelector.
			switch (init.previewInit?.forceWebRender) {
			case "download" :
			case "window" :
				init.remoteResSelector = (reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient) => new RemoteRes(reg, resDesc);
				break;
			case "video" :
				init.remoteResSelector = REMOTE.remoteResAsVideo;
				break;
			case "audio" :
				init.remoteResSelector = REMOTE.remoteResAsAudio;
				break;
			default:
				init.remoteResSelector = REMOTE.remoteResForPreview;
			}
		}
		this.config = init;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.buildStruct(init);
	}

	protected buildStruct(init: OTxtMetaRemoteInit) {
		this.titleRoot = <div id="title"/>;
		if (!init.metaBtn || init.metaBtn === "shown") {
			this.metaBtn = new Button().initialize({reg: this.config.reg, title: "Métadonnées d'accès à cette ressource...", icon: "/@skin@/edit/wed/txt/showMeta.svg", uiContext: "bar"});
			this.metaBtn.id = "metaBtn";
			this.metaBtn.onclick = this.onMetaBtn;
		}
		if (!init.openBtn || init.openBtn === "shown") {
			this.openBtn = new Button().initialize({reg: this.config.reg, title: "Ouvrir cette ressource...", icon: "/@skin@/core/widgets/remote/externalOpen.svg", uiContext: "bar"});
			this.openBtn.hidden = true;
			this.openBtn.id = "openBtn";
			this.openBtn.onclick = this.onopenBtn;
		}
		if (this.metaBtn || this.openBtn) {
			this.shadowRoot.appendChild(<div id="head">{this.titleRoot}{this.metaBtn}{this.openBtn}</div>);
		} else {
			this.shadowRoot.append(this.titleRoot);
		}

		if (!init.preview || init.preview === 'shown') {
			this.previewRoot = <RemoteResRenderer id="preview" î={BASIS.newInit(init.previewInit, init.reg)}/> as RemoteResRenderer;
		} else if (init.preview === 'tooltip') {
			POPUP.promiseTooltip(this, async (txtElt) => {
				return new TxtMetaRemoteTt().initialize({
					txtElt: this.config.txtElt,
					buildTitle: () => 'done',
					metaBtn: "none",
					openBtn: "none",
					previewInit: this.config.previewInit
				})
			}, {hoverAllowed: true});
		}
		if (this.previewRoot) this.shadowRoot.appendChild(this.previewRoot);
	}

	onMetaBtn() {
		const me = DOMSH.findHost<TxtMetaRemote>(this);
		OpenMetaDialog.openMetaDialog(me.config.txtElt);
	}

	onopenBtn(this: Button, ev: MouseEvent) {
		const me = DOMSH.findHost<TxtMetaRemote>(this);
		const view = me.findOpenView();
		if (view) window.open(REMOTE.buildUrlFromResDesc(me.lastRemoteDesc, view), "_blank");
	}
}


/**
 * Affichage des metas avec un lien remote en objet inline : pour emptyLeaf.
 */
export class TxtMetaRemoteInline extends TxtMetaRemote {
}

REG.reg.registerSkin("wsp-txtmetaremoteinline", 1, /* language=CSS */ `
	:host {
		display: inline;
		border: 1px solid var(--border-color);
		cursor: pointer;
		background-color: var(--alt1-bgcolor);
		max-height: 3em;
	}

	[hidden] {
		display: none;
	}

	#head,
	#title {
		display: inline;
	}

	c-button {
		display: inline-flex;
		--icon-size: 1em;
	}

	#preview {
		display: inline-flex;
	}
`);
customElements.define("wsp-txtmetaremoteinline", TxtMetaRemoteInline);


/**
 * Affichage des metas avec un lien remote en Block : pour emptyBlock.
 */
export class TxtMetaRemoteBlock extends TxtMetaRemote {
}

REG.reg.registerSkin("wsp-txtmetaremoteblock", 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: column;
		border: 1px solid var(--border-color);
		border-radius: 4px;
		cursor: pointer;
		margin: 0 3%;
		background-color: var(--alt1-bgcolor);
	}

	[hidden] {
		display: none;
	}

	#head {
		display: flex;
	}

	#head > #title {
		flex: 1;
	}

	#title {
		text-align: center;
	}
`);
customElements.define("wsp-txtmetaremoteblock", TxtMetaRemoteBlock);


/**
 * Affichage des metas avec un lien remote en Tooltip : pour phraseTag ou textLeafTag
 */
export class TxtMetaRemoteTt extends TxtMetaRemoteBlock {
}

REG.reg.registerSkin("wsp-txtmetaremotett", 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: column;
		background-color: var(--alt1-bgcolor);
		padding: .5em;
	}

	[hidden] {
		display: none;
	}

	#head {
		display: flex;
	}

	#head > #title {
		flex: 1;
	}

	#title {
		text-align: center;
	}
`);
customElements.define("wsp-txtmetaremotett", TxtMetaRemoteTt);